import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

interface UseLanguagePreferenceReturn {
  showLanguageModal: boolean;
  hasLanguagePreference: boolean;
  handleLanguageSelect: (language: string) => void;
  handleSkipLanguageSelection: () => void;
}

export const useLanguagePreference = (): UseLanguagePreferenceReturn => {
  const { i18n } = useTranslation();

  // Initialize state synchronously to ensure modal appears immediately if needed
  const [showLanguageModal, setShowLanguageModal] = useState(() => {
    const storedPreference = localStorage.getItem("vett-language-preference");
    // Show modal only if there is no stored preference (and it hasn't been skipped)
    return !storedPreference;
  });

  const [hasLanguagePreference, setHasLanguagePreference] = useState(() => {
    const storedPreference = localStorage.getItem("vett-language-preference");
    return !!storedPreference;
  });

  useEffect(() => {
    const storedPreference = localStorage.getItem("vett-language-preference");

    // Sync language if preference exists and is different from current
    if (
      storedPreference &&
      storedPreference !== "skipped" &&
      storedPreference !== i18n.language
    ) {
      i18n.changeLanguage(storedPreference);
    }
  }, [i18n]);

  const handleLanguageSelect = (language: string) => {
    setShowLanguageModal(false);
    setHasLanguagePreference(true);

    // Store the preference
    localStorage.setItem("vett-language-preference", language);
    localStorage.setItem("i18nextLng", language);

    // Change language if different
    if (language !== i18n.language) {
      i18n.changeLanguage(language);
    }
  };

  const handleSkipLanguageSelection = () => {
    setShowLanguageModal(false);
    setHasLanguagePreference(true);

    // Mark as skipped so we don't show again
    localStorage.setItem("vett-language-preference", "skipped");
  };

  return {
    showLanguageModal,
    hasLanguagePreference,
    handleLanguageSelect,
    handleSkipLanguageSelection,
  };
};
